// src/services/postService.ts

import { auth } from '../config/firebase';
import { Post } from '../types/calendar';
import { logger } from '../utils/logger';
import { PostRequestError, readPostError } from '../utils/postErrors';

const API_BASE_URL = 'https://api.socialness.ai';
// The public gateway does not currently allow Authorization in CORS preflights.
// Use the management service's configured public domain for post CRUD.
const POSTS_URL = 'https://management.socialness.ai/posts';

/**
 * Creates a post on socialness.ai
 * @param basePayload - Object containing post details (content, brandId, platforms, etc.)
 * @returns The JSON response from the API if successful
 */
export const createPost = async (postData: any, isFormData: boolean = false) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('You must be logged in to create a post');
    }
    
    const token = await user.getIdToken();
    
    const url = POSTS_URL;
    logger.info('postService', `Creating post at ${url}`);
    
    const headers: HeadersInit = {
      'Authorization': `Bearer ${token}`
    };
    
    // Only add Content-Type if not FormData
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    
    // Handle body preparation based on type
    let body;
    if (isFormData) {
      body = postData;
      
      if (postData instanceof FormData) {
        const fileEntry = postData.get('file');
        if (fileEntry instanceof File) {
          logger.info('postService', `Uploading file: ${fileEntry.name} (${fileEntry.size} bytes)`);
        }
      }
    } else {
      body = JSON.stringify(postData);
    }
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body
    });
    
    if (!response.ok) {
      throw await readPostError(response, 'Failed to create post');
    }
    
    const data = await response.json();
    logger.info('postService', 'Post created successfully');
    return data;
  } catch (error) {
    logger.error('postService', 'Error in createPost', error);
    throw error;
  }
};

/**
 * Get a post by its ID
 * @param postId The ID of the post to retrieve
 * @returns The post data
 */
export const getPost = async (postId: string): Promise<Post> => {
  try {
    logger.info('postService', `Fetching post with ID: ${postId}`);
    
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const url = `${POSTS_URL}/${encodeURIComponent(postId)}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      logger.error('postService', `Error fetching post ${postId}: ${response.status}`, errorText);
      
      if (response.status === 404) {
        throw new Error(`Post with ID "${postId}" could not be found. It may have been deleted or never existed.`);
      }
      
      try {
        const errorJson = JSON.parse(errorText);
        throw new Error(errorJson.message || `Failed to get post: ${response.status}`);
      } catch (parseError) {
        throw new Error(`Failed to get post: ${response.status} - ${errorText || response.statusText}`);
      }
    }

    const data = await response.json();
    
    // Check for specific API response structures
    let postData = data;
    
    if (data.post && typeof data.post === 'object') {
      postData = data.post;
    } else if (data.data && typeof data.data === 'object') {
      if (data.data.post && typeof data.data.post === 'object') {
        postData = data.data.post;
      } else {
        postData = data.data;
      }
    }
    
    // Check for required fields
    if (!postData.id) logger.warn('postService', 'Post data has no id');
    if (!postData.brandId) logger.warn('postService', 'Post data has no brandId');
    if (!postData.content && postData.content !== '') logger.warn('postService', 'Post data has no content property');
    if (!postData.platforms || !Array.isArray(postData.platforms)) logger.warn('postService', 'Post data has invalid platforms');
    
    logger.info('postService', 'Post fetched successfully');
    return postData;
  } catch (error) {
    logger.error('postService', `Error getting post ${postId}`, error);
    throw error;
  }
};

/**
 * Update an existing post
 * @param postId ID of the post to update
 * @param postData Post data to update
 * @param isFormData Whether the postData is FormData (for file uploads)
 * @returns The updated post
 */
export const updatePost = async (postId: string, postData: any, isFormData: boolean = false) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('You must be logged in to update a post');
    }
    
    const token = await user.getIdToken();
    
    const url = `${POSTS_URL}/${encodeURIComponent(postId)}`;
    logger.info('postService', `Updating post ${postId}`);
    
    const headers: HeadersInit = {
      'Authorization': `Bearer ${token}`
    };
    
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    
    let body;
    if (isFormData) {
      body = postData;
      
      if (postData instanceof FormData) {
        const fileEntry = postData.get('file');
        if (fileEntry instanceof File) {
          logger.info('postService', `Updating with file: ${fileEntry.name} (${fileEntry.size} bytes)`);
        }
      }
    } else {
      body = JSON.stringify(postData);
    }
    
    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body
    });
    
    if (!response.ok) {
      throw await readPostError(response, 'Failed to update post');
    }
    
    const data = await response.json();
    logger.info('postService', 'Post updated successfully');
    return data;
  } catch (error) {
    logger.error('postService', 'Error in updatePost', error);
    throw error;
  }
};

/**
 * Checks the status of a post
 * @param postId The post ID
 * @returns The status response from the API
 */
export const checkPostStatus = async (postId: string) => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(
      `${POSTS_URL}/${encodeURIComponent(postId)}/status`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    logger.info('postService', `Post status checked for ${postId}`);
    return await response.json();
  } catch (error) {
    logger.error('postService', 'Error checking post status', error);
    throw error;
  }
};

/**
 * Deletes a post
 * @param postId The ID of the post to delete
 * @returns Promise with the deletion result
 */
export const deletePost = async (postId: string): Promise<{ success: boolean; message?: string; status?: number; code?: string }> => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(
      `${POSTS_URL}/${encodeURIComponent(postId)}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw await readPostError(response, 'Failed to delete post');
    }

    logger.info('postService', `Post ${postId} deleted successfully`);
    return { success: true };
  } catch (error) {
    logger.error('postService', `Error deleting post ${postId}`, error);
    return { 
      success: false, 
      message: error instanceof Error ? error.message : 'Unknown error occurred',
      ...(error instanceof PostRequestError ? { status: error.status, code: error.code } : {}),
    };
  }
};

/**
 * Submits a review decision for a post.
 * @param postId The ID of the post being reviewed.
 * @param reviewStatus The decision ('approved' or 'rejected').
 * @param reviewComment Optional comment from the reviewer.
 * @returns The API response.
 */
export const submitPostReview = async (
  postId: string, 
  reviewStatus: 'approved' | 'rejected', 
  reviewComment: string
): Promise<any> => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const url = `${API_BASE_URL}/management/posts/${encodeURIComponent(postId)}/submit-review`;
    logger.info('postService', `Submitting ${reviewStatus} review for post ${postId}`);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        reviewStatus,
        reviewComment,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      logger.error('postService', `Review submission failed: ${response.status}`, errorText);
      try {
        const errorJson = JSON.parse(errorText);
        throw new Error(errorJson.message || `Failed to submit review: ${response.status}`);
      } catch (parseError) {
        throw new Error(`Failed to submit review: ${response.status} - ${errorText || response.statusText}`);
      }
    }

    const data = await response.json();
    logger.info('postService', `Review submitted successfully for post ${postId}`);
    return data;

  } catch (error) {
    logger.error('postService', `Error submitting review for post ${postId}`, error);
    if (error instanceof Error) {
       throw new Error(`Failed to submit review: ${error.message}`);
    } else {
       throw new Error('An unknown error occurred while submitting the review.');
    }
  }
};

/**
 * Bulk update multiple posts
 * @param postIds Array of post IDs to update
 * @param updateData Data to update (status, brandId, platforms, etc.)
 * @returns Array of update results
 */
export const bulkUpdatePosts = async (
  postIds: string[], 
  updateData: {
    status?: string;
    brandId?: string;
    platforms?: string[];
  }
): Promise<{ success: boolean; updated: number; failed: string[]; errors: any[] }> => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const url = `${API_BASE_URL}/management/posts/bulk-update`;
    logger.info('postService', `Bulk updating ${postIds.length} posts`);

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        postIds,
        updateData
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      logger.error('postService', `Bulk update failed: ${response.status}`, errorText);
      
      // If bulk endpoint doesn't exist, fall back to individual updates
      if (response.status === 404 || response.status === 405) {
        logger.info('postService', 'Bulk endpoint not available, falling back to individual updates');
        return await bulkUpdatePostsFallback(postIds, updateData);
      }
      
      try {
        const errorJson = JSON.parse(errorText);
        throw new Error(errorJson.message || `Bulk update failed: ${response.status}`);
      } catch (parseError) {
        throw new Error(`Bulk update failed: ${response.status} - ${errorText || response.statusText}`);
      }
    }

    const data = await response.json();
    logger.info('postService', `Bulk update completed: ${data.updated} updated, ${data.failed?.length || 0} failed`);
    return data;

  } catch (error) {
    logger.error('postService', 'Error in bulk update', error);
    
    // If there's a network error or other issue, try fallback
    logger.info('postService', 'Attempting fallback to individual updates');
    return await bulkUpdatePostsFallback(postIds, updateData);
  }
};

/**
 * Fallback function for bulk updates using individual API calls
 * @param postIds Array of post IDs to update
 * @param updateData Data to update
 * @returns Update results
 */
const bulkUpdatePostsFallback = async (
  postIds: string[], 
  updateData: {
    status?: string;
    brandId?: string;
    platforms?: string[];
  }
): Promise<{ success: boolean; updated: number; failed: string[]; errors: any[] }> => {
  const results = {
    success: true,
    updated: 0,
    failed: [] as string[],
    errors: [] as any[]
  };

  // Process updates sequentially to avoid overwhelming the API
  for (const postId of postIds) {
    try {
      await updatePost(postId, updateData, false);
      results.updated++;
      logger.info('postService', `Successfully updated post ${postId}`);
    } catch (error) {
      results.failed.push(postId);
      results.errors.push({ postId, error: error instanceof Error ? error.message : 'Unknown error' });
      logger.error('postService', `Failed to update post ${postId}`, error);
    }
  }

  // Consider it a success if at least some posts were updated
  results.success = results.updated > 0;
  
  logger.info('postService', `Fallback bulk update completed: ${results.updated} updated, ${results.failed.length} failed`);
  return results;
};
